// frontend/src/features/portfolio-diversification-optimizer-pro/components/CorrelationContagionPanel.tsx
//
// Pillar 1 -- Correlation & Tail Contagion. Renders the 63-day rolling
// correlation regime gauge, the asymmetric normal-vs-tail contagion summary,
// an interactive heatmap (Normal Ledoit-Wolf / Downside 15% Tail / Contagion
// Spread Delta-rho) with an HRP quasi-diagonal reordering toggle and a hovered
// pair inspector, plus the top redundant / diversifying pairs and the HRP
// hierarchical linkage tree.
//
// Themed for the shared light/dark dashboard shell.

import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  GitCompare,
  GitMerge,
  Grid,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import type { CorrelationAnalysis, RegimeStatus } from "../types";

interface CorrelationContagionPanelProps {
  correlation: CorrelationAnalysis;
  tickers: string[];
  hrpOrder: string[];
}

type MatrixMode = "normal" | "tail" | "spread";

function getCorrelationCellColor(val: number, mode: MatrixMode, isDiagonal: boolean): string {
  if (isDiagonal) {
    return "bg-slate-200/80 text-slate-500 border-slate-300 dark:bg-slate-800/90 dark:text-slate-400 dark:border-slate-700/60";
  }

  if (mode === "spread") {
    // Spread = Tail Corr - Normal Corr. Positive means correlation spikes in crashes (bad).
    if (val >= 0.25) return "bg-rose-500/40 text-rose-700 border-rose-500/40 dark:text-rose-100";
    if (val >= 0.12) return "bg-amber-500/30 text-amber-700 border-amber-500/30 dark:text-amber-100";
    if (val >= 0.04) return "bg-amber-500/15 text-amber-700 border-amber-500/20 dark:text-amber-200";
    if (val <= -0.1) return "bg-emerald-500/30 text-emerald-700 border-emerald-500/30 dark:text-emerald-100";
    if (val < -0.02) return "bg-emerald-500/15 text-emerald-700 border-emerald-500/20 dark:text-emerald-200";
    return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/80 dark:text-slate-300 dark:border-slate-800";
  }

  // Normal or Tail correlation [-1, 1].
  if (val >= 0.8) return "bg-rose-500/45 text-rose-700 border-rose-500/40 dark:text-rose-100";
  if (val >= 0.6) return "bg-amber-500/35 text-amber-700 border-amber-500/30 dark:text-amber-100";
  if (val >= 0.35) return "bg-amber-500/15 text-amber-700 border-amber-500/20 dark:text-amber-200";
  if (val <= -0.25) return "bg-emerald-500/40 text-emerald-700 border-emerald-500/40 dark:text-emerald-100";
  if (val < 0.0) return "bg-teal-500/20 text-teal-700 border-teal-500/30 dark:text-teal-200";
  return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/75 dark:text-slate-300 dark:border-slate-800";
}

function getRegimeMeta(status: RegimeStatus) {
  switch (status) {
    case "contagion_stress":
      return {
        label: "Contagion Stress Regime",
        badgeClass: "bg-rose-500/15 text-rose-700 ring-rose-500/30 dark:text-rose-300",
        barColor: "bg-rose-500",
        description:
          "Rolling 63-day pairwise correlation is near historical 95th percentile highs. Cross-asset diversification is compressed.",
      };
    case "elevated":
      return {
        label: "Elevated Correlation Regime",
        badgeClass: "bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-300",
        barColor: "bg-amber-500",
        description:
          "Rolling correlation is meaningfully above its long-run structural average.",
      };
    case "low_correlation":
      return {
        label: "High-Dispersion / Low-Correlation Regime",
        badgeClass: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300",
        barColor: "bg-emerald-500",
        description:
          "Holdings are moving independently with below-average cross-asset co-movement.",
      };
    default:
      return {
        label: "Normal Structural Regime",
        badgeClass: "bg-sky-500/15 text-sky-700 ring-sky-500/30 dark:text-sky-300",
        barColor: "bg-sky-500",
        description:
          "Rolling 63-day correlation is tracking near its long-term historical mean.",
      };
  }
}

const cardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

export const CorrelationContagionPanel: React.FC<CorrelationContagionPanelProps> = ({
  correlation,
  tickers,
  hrpOrder,
}) => {
  const [matrixMode, setMatrixMode] = useState<MatrixMode>("normal");
  const [useHrpOrder, setUseHrpOrder] = useState<boolean>(true);
  const [hoveredCell, setHoveredCell] = useState<{
    row: string;
    col: string;
    normalVal: number;
    tailVal: number;
    spreadVal: number;
  } | null>(null);

  const orderedTickers = useMemo(() => {
    if (useHrpOrder && hrpOrder.length === tickers.length) {
      return hrpOrder;
    }
    return tickers;
  }, [useHrpOrder, hrpOrder, tickers]);

  const regimeMeta = getRegimeMeta(correlation.rolling_regime.regime_status);

  // Gauge position (0%..100%) across a [-0.2, 1.0] correlation scale.
  const scalePos = (val: number) => Math.min(100, Math.max(0, ((val + 0.2) / 1.2) * 100));

  return (
    <div className="space-y-6">
      {/* Top Row: Rolling Regime Gauge + Tail Contagion Summary */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Rolling 63-Day Regime Gauge */}
        <div className={`${cardClass} lg:col-span-7`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">
                {correlation.rolling_regime.window_days}-Trading-Day Rolling Correlation Regime
              </h3>
            </div>
            <span
              className={`rounded-full px-3 py-0.5 text-xs font-semibold ring-1 ${regimeMeta.badgeClass}`}
            >
              {regimeMeta.label}
            </span>
          </div>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{regimeMeta.description}</p>

          {/* Visual Range Bar */}
          <div className="mt-5 space-y-2">
            <div className="flex justify-between font-mono text-[11px] text-slate-500 dark:text-slate-400">
              <span>Hist Min: {correlation.rolling_regime.historical_min_correlation.toFixed(2)}</span>
              <span>Hist Mean: {correlation.rolling_regime.historical_mean_correlation.toFixed(2)}</span>
              <span>95th Pct: {correlation.rolling_regime.historical_p95_correlation.toFixed(2)}</span>
            </div>

            <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800/90">
              {/* Highlight band between Min and P95 */}
              <div
                className="absolute bottom-0 top-0 bg-slate-300/80 dark:bg-slate-700/60"
                style={{
                  left: `${scalePos(correlation.rolling_regime.historical_min_correlation)}%`,
                  width: `${Math.max(
                    2,
                    scalePos(correlation.rolling_regime.historical_p95_correlation) -
                      scalePos(correlation.rolling_regime.historical_min_correlation)
                  )}%`,
                }}
              />
              {/* Mean Marker */}
              <div
                className="absolute bottom-0 top-0 w-0.5 bg-slate-500 dark:bg-slate-300"
                style={{
                  left: `${scalePos(correlation.rolling_regime.historical_mean_correlation)}%`,
                }}
                title="Historical Mean Correlation"
              />
              {/* Current Marker */}
              <div
                className={`absolute bottom-0 top-0 -ml-1 w-2 rounded-full ${regimeMeta.barColor} ring-2 ring-white dark:ring-slate-900`}
                style={{
                  left: `${scalePos(correlation.rolling_regime.current_avg_correlation)}%`,
                }}
                title="Current 63-Day Rolling Correlation"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-xs">
              <span className="text-slate-500 dark:text-slate-400">
                Current {correlation.rolling_regime.window_days}d Avg Correlation:{" "}
                <strong className="font-mono text-slate-900 dark:text-white">
                  {correlation.rolling_regime.current_avg_correlation.toFixed(3)}
                </strong>
              </span>
              <span className="text-slate-500 dark:text-slate-400">
                Ledoit-Wolf Shrinkage Intensity δ:{" "}
                <strong className="font-mono text-indigo-600 dark:text-indigo-400">
                  {(correlation.shrinkage_intensity * 100).toFixed(1)}%
                </strong>
              </span>
            </div>
          </div>
        </div>

        {/* Normal vs Downside Tail Contagion Comparison */}
        <div className={`${cardClass} flex flex-col justify-between lg:col-span-5`}>
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
                <GitCompare className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                Asymmetric Tail Contagion Diagnostics
              </span>
              <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                Worst 15% Days
              </span>
            </div>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              Compares full-sample Ledoit-Wolf correlation against empirical correlation during the worst 15% portfolio drawdown sessions.
            </p>
          </div>

          <div className="my-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/60">
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Normal Corr</div>
              <div className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
                {correlation.average_normal_correlation.toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/60">
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Tail Crash Corr</div>
              <div className="mt-1 font-mono text-xl font-bold text-amber-600 dark:text-amber-400">
                {correlation.average_tail_correlation.toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/60">
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Contagion Δρ</div>
              <div
                className={`mt-1 font-mono text-xl font-bold ${
                  correlation.tail_contagion_spread > 0.1
                    ? "text-rose-600 dark:text-rose-400"
                    : "text-emerald-600 dark:text-emerald-400"
                }`}
              >
                {correlation.tail_contagion_spread >= 0 ? "+" : ""}
                {correlation.tail_contagion_spread.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="text-[11px]">
            {correlation.tail_contagion_spread > 0.1 ? (
              <span className="flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400" />
                Correlations rise materially during sell-offs; inspect the Contagion Spread (Δρ) matrix below.
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500 dark:text-emerald-400" />
                Cross-asset correlations remain stable during downside tail sessions.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Interactive Correlation Matrix Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Grid className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                Cross-Asset Correlation &amp; Tail Contagion Matrix
              </h3>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Switch between Normal shrunk correlations, Downside Tail correlations, and the Contagion Spread (Δρ = ρ_tail − ρ_normal).
            </p>
          </div>

          {/* Matrix Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Mode Switcher */}
            <div className="inline-flex flex-wrap rounded-xl border border-slate-200 bg-slate-100 p-1 dark:border-slate-800 dark:bg-slate-950">
              {(
                [
                  { id: "normal", label: "Normal (Ledoit-Wolf)" },
                  { id: "tail", label: "Downside Tail (15%)" },
                  { id: "spread", label: "Contagion Spread (Δρ)" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setMatrixMode(tab.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    matrixMode === tab.id
                      ? "bg-indigo-600 text-white shadow"
                      : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* HRP Block-Diagonal Ordering Toggle */}
            <button
              type="button"
              onClick={() => setUseHrpOrder((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition ${
                useHrpOrder
                  ? "border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300"
                  : "border-slate-200 bg-slate-100 text-slate-500 hover:text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {useHrpOrder ? "HRP Quasi-Diagonal Order" : "Input Ticker Order"}
            </button>
          </div>
        </div>

        {/* Hovered Pair Inspector Bar */}
        <div className="mt-4 flex min-h-[36px] items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-2 text-xs dark:border-slate-800/80 dark:bg-slate-950/60">
          {hoveredCell ? (
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-mono font-bold text-slate-900 dark:text-white">
                {hoveredCell.row} × {hoveredCell.col}
              </span>
              <span className="text-slate-500 dark:text-slate-400">
                Normal ρ:{" "}
                <strong className="font-mono text-slate-700 dark:text-slate-200">
                  {hoveredCell.normalVal.toFixed(3)}
                </strong>
              </span>
              <span className="text-slate-500 dark:text-slate-400">
                Downside Tail ρ:{" "}
                <strong className="font-mono text-amber-600 dark:text-amber-300">
                  {hoveredCell.tailVal.toFixed(3)}
                </strong>
              </span>
              <span className="text-slate-500 dark:text-slate-400">
                Contagion Spread Δρ:{" "}
                <strong
                  className={`font-mono ${
                    hoveredCell.spreadVal > 0.1
                      ? "text-rose-600 dark:text-rose-400"
                      : hoveredCell.spreadVal < 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {hoveredCell.spreadVal >= 0 ? "+" : ""}
                  {hoveredCell.spreadVal.toFixed(3)}
                </strong>
              </span>
            </div>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">
              Hover over any cell in the matrix to inspect Normal vs. Downside Tail correlation and Contagion Spread.
            </span>
          )}
        </div>

        {/* Scrollable Heatmap Grid */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-center font-mono text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white p-2 text-left text-[11px] font-semibold uppercase text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  Asset
                </th>
                {orderedTickers.map((colTicker) => (
                  <th
                    key={colTicker}
                    className="min-w-[64px] p-2 text-[11px] font-bold text-slate-600 dark:text-slate-300"
                  >
                    {colTicker}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedTickers.map((rowTicker) => (
                <tr key={rowTicker}>
                  <td className="sticky left-0 z-10 bg-white p-2 text-left font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    {rowTicker}
                  </td>
                  {orderedTickers.map((colTicker) => {
                    const isDiag = rowTicker === colTicker;
                    const normVal = correlation.normal_matrix?.[rowTicker]?.[colTicker] ?? 0;
                    const tailVal = correlation.tail_matrix?.[rowTicker]?.[colTicker] ?? 0;
                    const spreadVal = tailVal - normVal;

                    const displayVal =
                      matrixMode === "normal"
                        ? normVal
                        : matrixMode === "tail"
                        ? tailVal
                        : spreadVal;

                    const colorClasses = getCorrelationCellColor(displayVal, matrixMode, isDiag);

                    return (
                      <td key={colTicker} className="p-1">
                        <div
                          onMouseEnter={() =>
                            setHoveredCell({
                              row: rowTicker,
                              col: colTicker,
                              normalVal: normVal,
                              tailVal,
                              spreadVal,
                            })
                          }
                          onMouseLeave={() => setHoveredCell(null)}
                          className={`cursor-default rounded-lg border px-1.5 py-2 font-mono text-xs transition hover:scale-105 ${colorClasses}`}
                        >
                          {isDiag && matrixMode === "spread"
                            ? "0.00"
                            : displayVal >= 0 && matrixMode === "spread"
                            ? `+${displayVal.toFixed(2)}`
                            : displayVal.toFixed(2)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Row: Top Redundant Pairs, Top Diversifiers, Hierarchical Linkage Steps */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Top Redundant Pairs */}
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <ArrowUpRight className="h-4 w-4 text-rose-500 dark:text-rose-400" />
              Highest Redundancy Pairs
            </h4>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">Co-moving bets</span>
          </div>
          <div className="mt-4 space-y-2.5">
            {correlation.top_redundant_pairs.map((pair) => (
              <div
                key={`${pair.ticker_a}-${pair.ticker_b}`}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-xs dark:border-slate-800/80 dark:bg-slate-950/60"
              >
                <div className="font-mono font-semibold text-slate-900 dark:text-white">
                  {pair.ticker_a} <span className="text-slate-400 dark:text-slate-500">↔</span> {pair.ticker_b}
                </div>
                <div className="flex items-center gap-3 font-mono">
                  <span className="font-bold text-rose-600 dark:text-rose-400">
                    ρ = {pair.normal_correlation.toFixed(2)}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    (Tail: {pair.tail_correlation.toFixed(2)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Diversifying Pairs */}
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <ShieldCheck className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
              Strongest Diversifying Pairs
            </h4>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">Drawdown dampeners</span>
          </div>
          <div className="mt-4 space-y-2.5">
            {correlation.top_diversifying_pairs.map((pair) => (
              <div
                key={`${pair.ticker_a}-${pair.ticker_b}`}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-xs dark:border-slate-800/80 dark:bg-slate-950/60"
              >
                <div className="font-mono font-semibold text-slate-900 dark:text-white">
                  {pair.ticker_a} <span className="text-slate-400 dark:text-slate-500">↔</span> {pair.ticker_b}
                </div>
                <div className="flex items-center gap-3 font-mono">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    ρ = {pair.normal_correlation.toFixed(2)}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    (Tail: {pair.tail_correlation.toFixed(2)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hierarchical Linkage Dendrogram Table */}
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <GitMerge className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              HRP Hierarchical Linkage Tree
            </h4>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">Metric distance d</span>
          </div>
          <div className="mt-4 max-h-60 space-y-2 overflow-y-auto pr-1">
            {correlation.dendrogram.map((node) => (
              <div
                key={node.step}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs dark:border-slate-800/80 dark:bg-slate-950/60"
              >
                <div className="flex items-center gap-2 truncate pr-2">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-indigo-500/15 font-mono text-[10px] font-bold text-indigo-600 dark:text-indigo-400">
                    {node.step}
                  </span>
                  <span
                    className="truncate font-mono text-slate-700 dark:text-slate-200"
                    title={`${node.left_label} + ${node.right_label}`}
                  >
                    {node.left_label} + {node.right_label}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2 font-mono text-[11px]">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    n={node.merged_size}
                  </span>
                  <span className="text-indigo-600 dark:text-indigo-300">d={node.distance.toFixed(3)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
